import { AdminRole } from "@repo/api-dtos"
import {
	createFileRoute,
	Link,
	useNavigate,
	useSearch,
} from "@tanstack/react-router"
import { format } from "date-fns"
import {
	ArrowLeft,
	FileText,
	Loader2,
	Pause,
	Play,
	RotateCw,
	Stamp,
	Trash2,
} from "lucide-react"
import * as React from "react"
import { z } from "zod"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
} from "@/components/layout"
import { CopyableText } from "@/components/marketplace/copyable-text"
import {
	formatEuroCents,
	PRODUCT_LABELS,
	STAMP_STATUS_LABELS,
	STAMP_STATUSES,
} from "@/components/postage-stamps/labels"
import { ResolveReviewDialog } from "@/components/postage-stamps/resolve-review-dialog"
import { SheetStatusBadge } from "@/components/postage-stamps/stamp-status-badge"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Skeleton } from "@/components/ui/skeleton"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import type { PaletteCommand } from "@/features/command-palette/types"
import { useRegisterPaletteCommands } from "@/features/command-palette/use-register-palette-commands"
import {
	QuickFilter,
	QuickFilterGroup,
} from "@/features/moderation-admin/QuickFilter"
import { StampsGrid } from "@/features/postage-stamps/StampsGrid"
import {
	useDeleteStampSheet,
	useRerenderSheetLabels,
	useRestoreStamp,
	useRetryStampSheet,
	useSetStampSheetActive,
	useSheetStamps,
	useStampSheet,
	useVoidStamp,
} from "@/queries/marketplace/use-postage-stamps"
import type { AdminPostageStamp } from "@/types/marketplace.types"

const searchSchema = z.object({
	status: z.enum(STAMP_STATUSES).optional().catch(undefined),
})

export const Route = createFileRoute("/postage-stamps/$sheetId")({
	component: SheetDetailPage,
	validateSearch: (search: Record<string, unknown>) =>
		searchSchema.parse(search),
})

function SheetDetailPage() {
	const { sheetId } = Route.useParams()
	const search = useSearch({ from: "/postage-stamps/$sheetId" })
	const navigate = useNavigate()
	const { hasRole } = useAdminRolesContext()
	const canManage = hasRole(AdminRole.MarketplaceOrderSupport)
	const sheet = useStampSheet(sheetId)
	const stamps = useSheetStamps({ sheetId, status: search.status })
	const setActive = useSetStampSheetActive()
	const retry = useRetryStampSheet()
	const rerender = useRerenderSheetLabels()
	const voidStamp = useVoidStamp()
	const restoreStamp = useRestoreStamp()
	const deleteSheet = useDeleteStampSheet()
	const [reviewing, setReviewing] = React.useState<AdminPostageStamp | null>(
		null,
	)
	const [confirmingDelete, setConfirmingDelete] = React.useState(false)
	const [confirmingRerender, setConfirmingRerender] = React.useState(false)

	const data = sheet.data
	const active = data?.active ?? false
	const canRetry = canManage && data?.status === "failed"
	const canToggleHold = canManage && (data?.unitsIngested ?? 0) > 0
	const canRerender = canManage && (data?.unitsIngested ?? 0) > 0
	const canDelete =
		canManage &&
		data !== undefined &&
		data.status !== "pending" &&
		data.status !== "processing"
	const retryIngestion = () => retry.mutate(sheetId)
	const toggleHold = () => setActive.mutate({ sheetId, active: !active })
	const confirmRerender = () =>
		rerender.mutate(sheetId, {
			onSuccess: () => setConfirmingRerender(false),
		})
	const confirmDelete = () =>
		deleteSheet.mutate(sheetId, {
			onSuccess: () => {
				setConfirmingDelete(false)
				navigate({ to: "/postage-stamps" })
			},
		})

	useRegisterPaletteCommands("postage-stamp-sheet", () => {
		const commands: PaletteCommand[] = []
		if (canRetry) {
			commands.push({
				id: "page:postage-stamp-sheet:retry",
				label: "Retry ingestion",
				group: "page",
				icon: RotateCw,
				perform: (ctx) => {
					retryIngestion()
					ctx.close()
				},
			})
		}
		if (canToggleHold) {
			commands.push({
				id: "page:postage-stamp-sheet:hold",
				label: active ? "Put on hold" : "Resume selling",
				group: "page",
				icon: active ? Pause : Play,
				perform: (ctx) => {
					toggleHold()
					ctx.close()
				},
			})
		}
		if (canRerender) {
			commands.push({
				id: "page:postage-stamp-sheet:rerender",
				label: "Re-render labels…",
				group: "page",
				icon: FileText,
				perform: (ctx) => {
					setConfirmingRerender(true)
					ctx.close()
				},
			})
		}
		if (canDelete) {
			commands.push({
				id: "page:postage-stamp-sheet:delete",
				label: "Delete sheet…",
				group: "page",
				icon: Trash2,
				perform: (ctx) => {
					setConfirmingDelete(true)
					ctx.close()
				},
			})
		}
		return commands
	}, [canRetry, canToggleHold, canRerender, canDelete, active, sheetId])

	if (sheet.isLoading) {
		return (
			<div className="flex flex-col gap-6 p-4 sm:p-6">
				<div className="flex flex-col gap-2">
					<Skeleton className="h-7 w-[240px] max-w-full" />
					<Skeleton className="h-5 w-[480px] max-w-full" />
				</div>
				<div className="flex flex-col gap-1">
					{Array.from({ length: 6 }, (_, index) => (
						<Skeleton key={index} className="h-[52px] w-full" />
					))}
				</div>
			</div>
		)
	}
	if (sheet.isError || !data) {
		return (
			<div className="p-4 sm:p-6">
				<p className="rounded-lg bg-bad-soft px-3 py-2 text-bad">
					Failed to load the sheet: {sheet.error?.message ?? "not found"}
				</p>
			</div>
		)
	}

	const setStatusFilter = (status: AdminPostageStamp["status"] | undefined) =>
		navigate({
			to: "/postage-stamps/$sheetId",
			params: { sheetId },
			search: { status },
		})

	return (
		<div className="flex flex-col gap-6 p-4 sm:p-6">
			<div className="flex flex-col gap-3">
				<div>
					<Button
						variant="ghost"
						size="sm"
						className="-ml-2 text-muted-foreground"
						asChild
					>
						<Link to="/postage-stamps">
							<ArrowLeft />
							Postage stamps
						</Link>
					</Button>
				</div>
				<div className="flex flex-wrap items-start justify-between gap-4">
					<div className="flex min-w-0 flex-col gap-1">
						<h1 className="flex min-w-0 flex-wrap items-center gap-3 text-2xl font-semibold tracking-[-0.01em]">
							<span className="min-w-0 truncate">
								{data.laPosteOrderRef ?? data.originalFilename ?? "Stamp sheet"}
							</span>
							<span className="text-sm font-normal">
								<SheetStatusBadge status={data.status} />
							</span>
							{data.status === "processing" ? (
								<Progress value={data.progress} className="w-32" />
							) : null}
						</h1>
						<CopyableText text={data.id} className="self-start" />
					</div>
					{canManage ? (
						<div className="flex flex-wrap items-center gap-2">
							{canRetry ? (
								<Button
									variant="outline"
									disabled={retry.isPending}
									onClick={retryIngestion}
								>
									<RotateCw />
									Retry ingestion
								</Button>
							) : null}
							{canToggleHold ? (
								<Button
									variant="outline"
									disabled={setActive.isPending}
									onClick={toggleHold}
								>
									{active ? <Pause /> : <Play />}
									{active ? "Put on hold" : "Resume selling"}
								</Button>
							) : null}
							{canRerender ? (
								<Button
									variant="outline"
									disabled={rerender.isPending}
									onClick={() => setConfirmingRerender(true)}
								>
									<FileText />
									Re-render labels
								</Button>
							) : null}
							{canDelete ? (
								<Button
									variant="outline"
									className="text-bad hover:text-bad"
									disabled={deleteSheet.isPending}
									onClick={() => setConfirmingDelete(true)}
								>
									<Trash2 />
									Delete sheet
								</Button>
							) : null}
						</div>
					) : null}
				</div>
			</div>

			<div className="flex min-w-0 flex-col gap-4">
				{!active ? (
					<div className="flex flex-col gap-0.5 rounded-lg bg-warn-soft px-4 py-3">
						<span className="font-medium text-warn">This sheet is on hold</span>
						<span className="text-muted-foreground">
							Its available stamps are not offered to sellers until you resume
							it. Stamps already reserved or used are not affected.
						</span>
					</div>
				) : null}

				<DetailPanel title="Sheet" icon={FileText}>
					<div className="grid divide-y md:grid-cols-3 md:divide-x md:divide-y-0">
						<DetailPanelSection label="Stock">
							<KeyValueList>
								<KeyValueItem label="In stock">
									{data.unitsIngested} / {data.expectedUnits}
								</KeyValueItem>
								<KeyValueItem label="In review">
									{data.unitsInReview}
								</KeyValueItem>
								<KeyValueItem label="Pages">
									{data.pageCount ?? "–"}
								</KeyValueItem>
							</KeyValueList>
						</DetailPanelSection>
						<DetailPanelSection label="Product">
							<KeyValueList>
								<KeyValueItem label="Product">
									{PRODUCT_LABELS[data.product]}
								</KeyValueItem>
								<KeyValueItem label="Unit cost">
									{formatEuroCents(data.unitCostCents)}
								</KeyValueItem>
							</KeyValueList>
						</DetailPanelSection>
						<DetailPanelSection label="Ingestion">
							<KeyValueList>
								<KeyValueItem label="Uploaded">
									{format(new Date(data.createdAt), "PP p")}
								</KeyValueItem>
								<KeyValueItem label="Finished">
									{data.finishedAt
										? format(new Date(data.finishedAt), "PP p")
										: "running"}
								</KeyValueItem>
							</KeyValueList>
						</DetailPanelSection>
					</div>
					{data.errorMessage ? (
						<DetailPanelSection label="Ingestion notes" className="bg-bad-soft">
							<p className="whitespace-pre-line">{data.errorMessage}</p>
						</DetailPanelSection>
					) : null}
				</DetailPanel>

				<DetailPanel title="Stamps" icon={Stamp} flush>
					<div className="overflow-x-auto border-b px-4 py-2.5">
						<QuickFilterGroup aria-label="Stamp status">
							<QuickFilter
								active={!search.status}
								onClick={() => setStatusFilter(undefined)}
							>
								All
							</QuickFilter>
							{STAMP_STATUSES.map((status) => (
								<QuickFilter
									key={status}
									active={search.status === status}
									onClick={() => setStatusFilter(status)}
								>
									{STAMP_STATUS_LABELS[status]}
								</QuickFilter>
							))}
						</QuickFilterGroup>
					</div>

					{stamps.isLoading ? (
						<div className="flex flex-col gap-1 px-4 py-3">
							{Array.from({ length: 6 }, (_, index) => (
								<Skeleton key={index} className="h-[52px] w-full" />
							))}
						</div>
					) : stamps.isError ? (
						<p className="px-4 py-3 text-bad">
							Failed to load the stamps: {stamps.error.message}
						</p>
					) : (stamps.data?.items.length ?? 0) === 0 ? (
						<p className="px-4 py-3 text-muted-foreground">
							{search.status
								? "No stamp of this sheet has that status."
								: "The ingestion has not produced any stamp for this sheet."}
						</p>
					) : (
						<StampsGrid
							stamps={stamps.data?.items ?? []}
							sheetOnHold={!active}
							canManage={canManage}
							busy={voidStamp.isPending || restoreStamp.isPending}
							onReview={setReviewing}
							onVoid={(stamp) => voidStamp.mutate(stamp.id)}
							onRestore={(stamp) => restoreStamp.mutate(stamp.id)}
							borderless
						/>
					)}
				</DetailPanel>
			</div>

			<ResolveReviewDialog
				stamp={reviewing}
				onOpenChange={(open) => !open && setReviewing(null)}
			/>

			<AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete this sheet?</AlertDialogTitle>
						<AlertDialogDescription>
							Deletes the sheet, its stamps ({data.unitsIngested} in stock,{" "}
							{data.unitsInReview} in review), their images and the uploaded
							PDF. Their tracking numbers become free for a new upload. This
							cannot be undone, and it is refused while a stamp of the sheet is
							on an order.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deleteSheet.isPending}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							disabled={deleteSheet.isPending}
							onClick={(event) => {
								event.preventDefault()
								confirmDelete()
							}}
						>
							{deleteSheet.isPending ? (
								<Loader2 className="animate-spin" />
							) : null}
							Delete sheet
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog
				open={confirmingRerender}
				onOpenChange={setConfirmingRerender}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Re-render the labels of this sheet?
						</AlertDialogTitle>
						<AlertDialogDescription>
							Every used stamp of the sheet gets its label rendered again from
							the order's current addresses, under the same tracking number.
							Sellers who already printed must print the new file.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={rerender.isPending}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={rerender.isPending}
							onClick={(event) => {
								event.preventDefault()
								confirmRerender()
							}}
						>
							{rerender.isPending ? <Loader2 className="animate-spin" /> : null}
							Re-render labels
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
